/**
 * Strategy detail (ARCHITECTURE.md §8): the DSL config viewer (conditions +
 * schedule + risk), controls (lifecycle, dry-run toggle, export, edit-in-chat),
 * the per-strategy P&L snapshot, a price chart for the primary symbol, and the
 * run-history timeline that links into decision replay.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CandleChart, Card, formatDateTime } from "@richrich/ui";
import { useI18n } from "@richrich/i18n/react";
import {
  CurrencySchema,
  MarketSymbolSchema,
  type Currency,
  type MarketCandlesResponse,
  type StrategyDsl,
} from "@richrich/contracts";
import { safeParseStrategyDSL } from "@richrich/strategy-core";
import { PageShell } from "../../components/PageShell";
import { api, unwrap } from "../../lib/api";
import { settingsQuery } from "../../lib/query";
import { strategyDetailQuery } from "../../features/strategies/api";
import { AccountBinding } from "../../features/strategies/AccountBinding";
import { BacktestPanel } from "../../features/strategies/BacktestPanel";
import { DslConfigViewer } from "../../features/strategies/DslConfigViewer";
import { RunHistoryTimeline } from "../../features/strategies/RunHistoryTimeline";
import { StrategyControls } from "../../features/strategies/StrategyControls";
import { StrategyPnlPanel } from "../../features/strategies/StrategyPnlPanel";
import { StrategyPortfolioPanel } from "../../features/strategies/StrategyPortfolioPanel";
import { DryRunBadge, ModeBadge, StatusBadge } from "../../features/strategies/meta";

const BASE_CURRENCY_FALLBACK: Currency = CurrencySchema.parse("KRW");

export const Route = createFileRoute("/strategies/$id")({
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(strategyDetailQuery(params.id));
  },
  component: StrategyDetailPage,
});

function StrategyDetailPage() {
  const { t, locale } = useI18n();
  const { id } = Route.useParams();
  const strategy = useQuery(strategyDetailQuery(id));
  const settings = useQuery(settingsQuery);

  if (strategy.isPending) {
    return (
      <PageShell eyebrow={t("strategies.strategyEyebrow")} title={t("common.loading")} loading />
    );
  }

  if (strategy.isError || strategy.data === undefined) {
    return (
      <PageShell eyebrow={t("strategies.strategyEyebrow")} title={t("strategies.notFound")}>
        <div className="rounded-xl border border-dashed border-hairline py-14 text-center">
          <p className="text-sm font-medium text-ink">{t("strategies.detailLoadError")}</p>
          <Link to="/strategies" className="mt-2 inline-block text-sm text-brand hover:underline">
            {t("strategies.backToStrategies")}
          </Link>
        </div>
      </PageShell>
    );
  }

  const s = strategy.data;
  const currency: Currency =
    s.budget?.currency ?? settings.data?.baseCurrency ?? BASE_CURRENCY_FALLBACK;

  // Parse once for the backtest price chart's indicator overlays; a malformed
  // DSL simply omits the overlays (the chart still renders).
  const parsedDsl = safeParseStrategyDSL(s.dsl);
  const dsl = parsedDsl.success ? parsedDsl.data : undefined;

  return (
    <PageShell
      eyebrow={t("strategies.detailEyebrow", { slug: s.slug })}
      title={s.name}
      actions={
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusBadge status={s.status} />
          <DryRunBadge dryRun={s.dryRun} />
          <ModeBadge mode={s.executionMode} />
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {/* Controls + runtime bindings */}
        <Card title={t("strategies.controlsTitle")} subtitle={t("strategies.controlsSubtitle")}>
          <div className="flex flex-col gap-4">
            <StrategyControls strategy={s} />
            <dl className="grid grid-cols-2 gap-x-8 gap-y-2 border-t border-hairline pt-3 sm:grid-cols-4">
              <Meta
                label={t("strategies.metaAccount")}
                value={s.accountSeq ?? t("strategies.metaUnbound")}
              />
              <Meta
                label={t("strategies.metaBudget")}
                value={s.budget !== null ? `${s.budget.amount} ${s.budget.currency}` : "—"}
              />
              <Meta
                label={t("strategies.metaNextRun")}
                value={s.nextRunAt !== null ? formatDateTime(s.nextRunAt, locale) : "—"}
              />
              <Meta
                label={t("strategies.metaUpdated")}
                value={formatDateTime(s.updatedAt, locale)}
              />
            </dl>
          </div>
        </Card>

        <Card
          title={t("strategies.accountBindingTitle")}
          subtitle={t("strategies.accountBindingSubtitle")}
        >
          <AccountBinding strategy={s} />
        </Card>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left: the definition */}
          <DslConfigViewer dsl={s.dsl} />

          {/* Right: performance, price, runs */}
          <div className="flex flex-col gap-6">
            <StrategyPortfolioPanel strategyId={s.id} />
            <StrategyPnlPanel strategyId={s.id} currency={currency} />
            <PrimarySymbolChart dsl={s.dsl} />
            <RunHistoryTimeline strategyId={s.id} limit={5} />
          </div>
        </div>

        {/* Backtest — full-width section under the definition/performance grid. */}
        <BacktestPanel strategy={s} dsl={dsl} />
      </div>
    </PageShell>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-muted">{label}</dt>
      <dd className="mt-0.5 truncate font-mono text-sm text-ink" title={value}>
        {value}
      </dd>
    </div>
  );
}

/** Price/candles chart for the strategy's primary traded symbol (§8). */
function PrimarySymbolChart({ dsl }: { dsl: StrategyDsl }) {
  const { t } = useI18n();
  const parsed = safeParseStrategyDSL(dsl);
  const rawSymbol = parsed.success ? parsed.data.universe.symbols[0]?.symbol : undefined;
  const symbolResult =
    rawSymbol !== undefined ? MarketSymbolSchema.safeParse(rawSymbol) : undefined;
  const symbol = symbolResult?.success ? symbolResult.data : undefined;

  const candles = useQuery({
    queryKey: ["market", "candles", symbol],
    queryFn: async (): Promise<MarketCandlesResponse> =>
      unwrap<MarketCandlesResponse>(
        await api.market.candles.$get({
          query: { symbol: symbol!, interval: "1d", count: "120" },
        }),
      ),
    enabled: symbol !== undefined,
    staleTime: 60_000,
  });

  if (symbol === undefined) {
    return (
      <Card title={t("strategies.priceTitle")} subtitle={t("strategies.primarySymbol")}>
        <p className="py-8 text-center text-sm text-muted">{t("strategies.noPrimarySymbol")}</p>
      </Card>
    );
  }

  return (
    <CandleChart
      title={t("strategies.priceWithSymbol", { symbol })}
      subtitle={t("strategies.dailyCandles")}
      candles={candles.data?.candles ?? []}
      loading={candles.isPending}
      height={280}
    />
  );
}
